import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
// Types
import { GetStream_stream } from 'api/streams/types/GetStream';
// Helpers
import { computeOrderStreamPath } from 'helpers/routes';
import {
  formatCountdown,
  isStartStreamButtonDisabled,
  getStreamWatchAvailableDate,
} from 'helpers/streams';
// UI3
import Button from 'ui3/Button/Button';
import LinkButton from 'ui3/LinkButton/LinkButton';
// Components
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
// Styles
import styles from './UpcomingWatchActions.module.scss';

type UpcomingWatchActionsProps = {
  stream: GetStream_stream;
  watchStreamPath: string;
  socialShareButtonText: string;
  // Viewers who can open the watch page directly (creator / purchased).
  // Others (e.g. free streams) go through the provided onWatch handler.
  hasDirectAccess: boolean;
  onWatch: () => void;
};

const UpcomingWatchActions = ({
  stream,
  watchStreamPath,
  socialShareButtonText,
  hasDirectAccess,
  onWatch,
}: UpcomingWatchActionsProps) => {
  // Re-render every second so the countdown ticks and the watch button
  // unlocks live once we cross the "1 hour before start" threshold.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(intervalId);
  }, []);

  // The watch button shares the same gating as the host "Start" button:
  // it stays locked until 1 hour before the scheduled start.
  const isWatchLocked = isStartStreamButtonDisabled(stream);

  const timeZoneOffset = stream.timeZone?.offset;
  const scheduleDate = stream.scheduleDate ?? undefined;
  const startMoment = timeZoneOffset
    ? dayjs(scheduleDate).subtract(timeZoneOffset, 'minute')
    : dayjs(scheduleDate);
  const remainingMs = startMoment.diff(dayjs(now));

  const availableDate = getStreamWatchAvailableDate(stream.scheduleDateUtc);

  const shareButton = (
    <SocialShareQRButton
      onlyIcon
      itemTitle={stream.name || ''}
      modalTitle="Share Stream"
      modalText="Share this stream in any way convenient for you"
      path={computeOrderStreamPath(stream)}
      twitterText={socialShareButtonText || ''}
    />
  );

  if (!isWatchLocked) {
    return (
      <div className={styles.root}>
        <div className={styles.actions}>
          {hasDirectAccess ? (
            <LinkButton
              link={watchStreamPath}
              variant="primary"
              className={styles.watchButton}
            >
              Watch Stream
            </LinkButton>
          ) : (
            <Button
              variant="primary"
              className={styles.watchButton}
              onClick={onWatch}
            >
              Watch Stream
            </Button>
          )}
          {shareButton}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        {/*
          CSS-only tooltip shown on wrapper hover. The disabled <button> gets
          `pointer-events: none` so hover falls through to the wrapper (a
          disabled button otherwise swallows pointer events over its box).
        */}
        <span className={styles.watchButtonWrapper}>
          <Button variant="primary" disabled className={styles.watchButton}>
            Watch Stream
          </Button>
          {availableDate && (
            <span role="tooltip" className={styles.tooltip}>
              {`Available to watch on ${availableDate}.`}
            </span>
          )}
        </span>
        {shareButton}
      </div>

      <p className={styles.countdown}>
        Stream starting in:{' '}
        <span className={styles.countdownValue}>
          {formatCountdown(remainingMs)}
        </span>
      </p>
    </div>
  );
};

export default UpcomingWatchActions;
